<script setup lang="ts">
  import { computed, onUnmounted, ref } from 'vue';

  const props = defineProps<{
    roles: string[];
  }>();

  const showTooltip = ref(false);
  const hideTimer = ref<ReturnType<typeof setTimeout> | null>(null);
  const anchorTop = ref(0);
  const anchorLeft = ref(0);

  const hasRoles = computed(() => props.roles.length > 0);
  const isSingle = computed(() => props.roles.length === 1);

  const tooltipStyle = computed(() => ({
    top: `${anchorTop.value - 8}px`,
    left: `${anchorLeft.value}px`,
  }));

  const cancelHide = () => {
    if (hideTimer.value) {
      clearTimeout(hideTimer.value);
      hideTimer.value = null;
    }
  };

  const scheduleHide = () => {
    cancelHide();
    hideTimer.value = setTimeout(() => {
      showTooltip.value = false;
    }, 200);
  };

  onUnmounted(cancelHide);

  const onTriggerEnter = (event: MouseEvent) => {
    const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
    anchorTop.value = rect.top;
    anchorLeft.value = rect.left + rect.width / 2;
    cancelHide();
    showTooltip.value = true;
  };

  const onTriggerLeave = () => scheduleHide();
  const onTooltipEnter = () => cancelHide();
  const onTooltipLeave = () => scheduleHide();
</script>

<template>
  <span v-if="!hasRoles" class="text-sm text-(--color-gray-07)">-</span>

  <span v-else-if="isSingle" class="text-sm text-(--color-gray-07)">
    {{ roles[0] }}
  </span>

  <div v-else class="inline-block">
    <span
      class="cursor-pointer text-sm text-(--color-gray-07) transition-colors duration-200 hover:text-(--color-badge-blue)"
      @mouseenter="onTriggerEnter"
      @mouseleave="onTriggerLeave"
    >
      {{ roles.length }} cargos
    </span>

    <div
      v-if="showTooltip"
      data-testid="cargos-tooltip"
      class="fixed z-[9999] w-64 -translate-x-1/2 -translate-y-full rounded-lg border border-(--color-border-color) bg-(--color-white-01) p-3 shadow-lg"
      :style="tooltipStyle"
      @mouseenter="onTooltipEnter"
      @mouseleave="onTooltipLeave"
    >
      <p class="mb-2 text-xs font-semibold text-(--color-gray-11)">Cargos ({{ roles.length }})</p>
      <ul class="max-h-72 space-y-1 overflow-y-auto pr-1">
        <li
          v-for="(role, index) in roles"
          :key="`${role}-${index}`"
          class="rounded px-2 py-1 text-xs bg-(--color-gray-09) text-(--color-gray-03)"
        >
          {{ role }}
        </li>
      </ul>
      <div
        class="absolute bottom-0 left-1/2 size-3 -translate-x-1/2 translate-y-1/2 rotate-45 border-b border-r border-(--color-border-color) bg-(--color-white-01)"
      />
    </div>
  </div>
</template>
